import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from "../firebase"; 
import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import ProtectedComponent from '../components/ProtectedComponent';

// ================================================================
// === 1. IMPORT the useAuth hook to check permissions          ===
// ================================================================
import { useAuth } from '../auth/AuthContext'; 

function ProductsPage() {
  // ================================================================
  // === 2. GET the user object from the context                  ===
  // ================================================================
  const { user } = useAuth();

  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ================================================================
  // === 3. ADD THE DIAGNOSTIC LOG                                ===
  // This will print the user's current permissions to the console.
  // ================================================================
  useEffect(() => {
    console.log("Permissions currently loaded for this user:", user?.permissions);
  }, [user]);


  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'MealsProducts'));
        const productsList = querySnapshot.docs.map((doc, index) => ({
          id: doc.id,
          productId: `PRO-${String(index + 1).padStart(4, '0')}`,
          ...doc.data()
        }));
        setProducts(productsList);
      } catch (err) {
        console.error("Error fetching products: ", err);
        setError('Failed to load products.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete the product "${name}"?`)) {
      try {
        await deleteDoc(doc(db, 'MealsProducts', id));
        setProducts(prevProducts => prevProducts.filter(product => product.id !== id));
        alert('Product deleted successfully!');
      } catch (err) {
        console.error("Error deleting product: ", err);
        alert('Failed to delete product.');
      }
    }
  };
  
  const handleEdit = (productId) => {
    navigate(`/edit/${productId}`);
  };

  if (loading) return <div style={styles.centered}>Loading products...</div>;
  if (error) return <div style={{ ...styles.centered, color: 'red' }}>{error}</div>;

  // The rest of the component's JSX is identical and correct.
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>Product List</h2>
        <ProtectedComponent requiredPermission="raw_products.create">
          <button style={styles.addButton} onClick={() => navigate('/add')}>
            <FaPlus style={{ marginRight: '8px' }} />
            Add New Product
          </button>
        </ProtectedComponent>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Product ID</th>
            <th style={styles.th}>Image</th>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Quantity</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.length > 0 ? (
            products.map(product => (
              <tr key={product.id}>
                <td style={styles.td}>{product.productId}</td>
                <td style={styles.td}><img src={product.imageUrl} alt={product.name} style={styles.image} /></td>
                <td style={styles.td}>{product.name}</td>
                <td style={styles.td}>{product.quantity || 0}</td>
                <td style={styles.td}>{product.categoryName || 'N/A'}</td>
                <td style={styles.td}>
                  <ProtectedComponent requiredPermission="raw_products.update">
                    <button style={{...styles.actionButton, ...styles.editButton}} onClick={() => handleEdit(product.id)}>
                      <FaEdit />
                    </button>
                  </ProtectedComponent>
                  <ProtectedComponent requiredPermission="raw_products.delete">
                    <button style={{...styles.actionButton, ...styles.deleteButton}} onClick={() => handleDelete(product.id, product.name)}>
                      <FaTrash />
                    </button>
                  </ProtectedComponent>
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="6" style={styles.centered}>No products found. Add one to get started!</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Styles remain the same
const styles = {
    container: { padding: '2rem', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' },
    addButton: { display: 'flex', alignItems: 'center', padding: '12px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8f9fa', padding: '12px 15px', borderBottom: '2px solid #dee2e6', textAlign: 'left', fontWeight: '600' },
    td: { padding: '12px 15px', borderBottom: '1px solid #eee', verticalAlign: 'middle' },
    image: { width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' },
    actionButton: { border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', color: 'white', marginRight: '5px' },
    editButton: { backgroundColor: '#28a745' },
    deleteButton: { backgroundColor: '#dc3545' },
    centered: { textAlign: 'center', padding: '20px', fontSize: '1rem', color: '#6c757d' },
};

export default ProductsPage;